/**
 * /tm-tickets — T&M Ticket list with filters.
 *
 * Status tabs: All / Draft / Eligible / Invoiced / Voided
 * Filters: project picker, date range, PCO number search
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button, Chip, Tab, Tabs, TextField,
  Table, TableBody, TableCell, TableHead, TableRow,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { tmTicketsListQueryOptions, projectsListQueryOptions } from "@/lib/queries";
import type { TMTicket, TMTicketStatus } from "@/lib/types";
import { ProjectIdSelect, STANDARD_PROJECT_STATUSES } from "@/components/ProjectIdSelect";

export const Route = createFileRoute("/_auth/tm-tickets/")({
  component: TMTicketsIndexPage,
});

type TabKey = "all" | "draft" | "eligible" | "invoiced" | "voided";

const STATUS_CHIP: Record<TMTicketStatus, { label: string; color: "default" | "warning" | "success" | "error" | "info" }> = {
  draft:    { label: "Draft",    color: "default"  },
  eligible: { label: "Eligible", color: "success"  },
  invoiced: { label: "Invoiced", color: "info"     },
  voided:   { label: "Voided",   color: "error"    },
};

function TMTicketsIndexPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("all");
  const [pcoFilter, setPcoFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");

  // Fetch all tickets matching header filters; status tab is client-side so counts stay accurate
  const ticketsQ = useQuery(tmTicketsListQueryOptions({
    pco_number: pcoFilter || undefined,
    project_id: projectFilter || undefined,
  }));

  // Label lookup for rows only; project selection itself is server-searched.
  const projectsQ = useQuery(projectsListQueryOptions({ limit: 500 }));
  const projectsById = useMemo(() => {
    const m: Record<string, string> = {};
    (projectsQ.data?.items ?? []).forEach((p) => { m[p.id] = `${p.code} — ${p.name}`; });
    return m;
  }, [projectsQ.data?.items]);

  const allTickets = ticketsQ.data?.items ?? [];

  const tickets = useMemo<TMTicket[]>(() => {
    if (tab === "all") return allTickets;
    return allTickets.filter((t) => t.status === tab);
  }, [allTickets, tab]);

  const tabCounts = useMemo(() => ({
    all:      allTickets.length,
    draft:    allTickets.filter((t) => t.status === "draft").length,
    eligible: allTickets.filter((t) => t.status === "eligible").length,
    invoiced: allTickets.filter((t) => t.status === "invoiced").length,
    voided:   allTickets.filter((t) => t.status === "voided").length,
  }), [allTickets]);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              T&amp;M Tickets
            </h1>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {allTickets.length} total
            </span>
          </div>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate({ to: "/tm-tickets/new" })}
          >
            New Ticket
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mt-3">
          <TextField
            size="small"
            label="PCO #"
            value={pcoFilter}
            onChange={(e) => setPcoFilter(e.target.value)}
            sx={{ width: 160 }}
          />
          <Box sx={{ width: 280 }}>
          <ProjectIdSelect
            label="Project"
            value={projectFilter}
            onChange={setProjectFilter}
            statusFilter={STANDARD_PROJECT_STATUSES}
            placeholder="All projects…"
          />
          </Box>
        </div>

        {/* Status tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as TabKey)}
          sx={{ mt: 1, minHeight: 36 }}
        >
          {(["all", "draft", "eligible", "invoiced", "voided"] as TabKey[]).map((key) => (
            <Tab
              key={key}
              value={key}
              label={`${key.charAt(0).toUpperCase() + key.slice(1)} (${tabCounts[key]})`}
              sx={{ minHeight: 36, py: 0.5, textTransform: "capitalize" }}
            />
          ))}
        </Tabs>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {ticketsQ.isLoading ? (
          <div className="p-6 text-slate-500">Loading…</div>
        ) : tickets.length === 0 ? (
          <div className="p-6 text-slate-500">No tickets found.</div>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Work Date</TableCell>
                <TableCell>PCO #</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Ticket #</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="center">Labor</TableCell>
                <TableCell align="center">Materials</TableCell>
                <TableCell align="center">Equipment</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((t) => {
                const chip = STATUS_CHIP[t.status] ?? { label: t.status, color: "default" };
                return (
                  <TableRow
                    key={t.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate({ to: "/tm-tickets/$ticketId", params: { ticketId: t.id } })}
                  >
                    <TableCell>{t.work_date}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t.pco_number}</TableCell>
                    <TableCell>{projectsById[t.project_id] ?? t.project_id}</TableCell>
                    <TableCell>{t.ticket_number ?? "—"}</TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{t.source}</TableCell>
                    <TableCell align="center">{t.labor.length}</TableCell>
                    <TableCell align="center">{t.materials.length}</TableCell>
                    <TableCell align="center">{t.equipment.length}</TableCell>
                    <TableCell>
                      <Chip
                        label={chip.label}
                        color={chip.color}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
